/**
 * Operations — the read-only health board for Quill's background machinery
 * (P5 Wave 4 contract §3). Four metric cards summarise the nightly loop, the
 * xAI catalog sync, the guardrail block-rate (with an inline SVG sparkline of
 * its daily trend), and the outstanding safety re-verification count. Below
 * them, the jobs table lists recent `quill_job_runs` with a left severity
 * stripe and a status pill (ok / partial / failed / running).
 *
 * There are NO mutations on this surface — it is purely observational. Each of
 * the four backend reads (summary, guardrail-rate, jobs, sync-log) loads
 * independently (Promise.allSettled) and renders its own error + Retry, so a
 * single failing endpoint never blanks the whole page or silently reads as
 * "no data". Card values prefer the `/summary` rollup but fall back to the
 * concrete datasets, so a partial summary still yields useful cards.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Grid, Alert, Button, Chip, Card, CardContent } from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import ShieldIcon from '@mui/icons-material/Shield';
import { useQuillHealth } from '../QuillHealthContext';
import MetricCard, { MetricColorTheme } from '../../common/MetricCard';
import DataTable, { Column } from '../../common/DataTable';
import { colors, typography, shadows } from '../../../theme/designTokens';
import {
  operationsApi,
  JobRun,
  SyncLogRow,
  GuardrailRatePoint,
  OperationsSummary,
} from './api';
import BlockRateSparkline from './BlockRateSparkline';

/** Coerce a node-pg count (bigint arrives as a string) to a number. */
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const SectionError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <Alert
    severity="error"
    action={
      <Button color="inherit" size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>
        Retry
      </Button>
    }
  >
    {message}
  </Alert>
);

type StatusColor = 'default' | 'success' | 'warning' | 'error' | 'info';

/** Chip colour + label for a job/nightly status. */
function statusPill(status?: string | null): { label: string; color: StatusColor } {
  switch ((status || '').toLowerCase()) {
    case 'ok':
      return { label: 'OK', color: 'success' };
    case 'partial':
      return { label: 'Partial', color: 'warning' };
    case 'failed':
      return { label: 'Failed', color: 'error' };
    case 'running':
      return { label: 'Running', color: 'info' };
    default:
      return { label: status || '—', color: 'default' };
  }
}

/** The severity-stripe colour for the left edge of a job row. */
function stripeColor(status?: string | null): string {
  switch ((status || '').toLowerCase()) {
    case 'ok':
      return colors.success.main;
    case 'partial':
      return colors.warning.main;
    case 'failed':
      return colors.error.main;
    case 'running':
      return colors.info.main;
    default:
      return colors.gray[300];
  }
}

function statusTheme(status?: string | null): MetricColorTheme {
  switch ((status || '').toLowerCase()) {
    case 'ok':
      return 'revenue';
    case 'failed':
      return 'costs';
    case 'partial':
    case 'running':
      return 'content';
    default:
      return 'default';
  }
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function relativeAge(iso?: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDuration(ms?: unknown): string {
  const n = num(ms);
  if (n <= 0) return '—';
  if (n < 1000) return `${n} ms`;
  const secs = n / 1000;
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const mins = Math.floor(secs / 60);
  const rem = Math.round(secs % 60);
  return `${mins}m ${rem}s`;
}

const OperationsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [summary, setSummary] = useState<OperationsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [rate, setRate] = useState<GuardrailRatePoint[]>([]);
  const [rateLoading, setRateLoading] = useState(true);
  const [rateError, setRateError] = useState<string | null>(null);

  const [jobs, setJobs] = useState<JobRun[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState<string | null>(null);

  const [syncLog, setSyncLog] = useState<SyncLogRow[]>([]);
  const [syncLoading, setSyncLoading] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await operationsApi.getSummary();
      if (mountedRef.current) setSummary(data);
    } catch (e) {
      if (mountedRef.current)
        setSummaryError(e instanceof Error ? e.message : 'Failed to load the operations summary.');
    } finally {
      if (mountedRef.current) setSummaryLoading(false);
    }
  }, []);

  const loadRate = useCallback(async () => {
    setRateLoading(true);
    setRateError(null);
    try {
      const data = await operationsApi.getGuardrailRate(14);
      if (mountedRef.current) setRate(data);
    } catch (e) {
      if (mountedRef.current)
        setRateError(e instanceof Error ? e.message : 'Failed to load the guardrail block-rate.');
    } finally {
      if (mountedRef.current) setRateLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    setJobsError(null);
    try {
      const data = await operationsApi.getJobs(50);
      if (mountedRef.current) setJobs(data);
    } catch (e) {
      if (mountedRef.current)
        setJobsError(e instanceof Error ? e.message : 'Failed to load recent job runs.');
    } finally {
      if (mountedRef.current) setJobsLoading(false);
    }
  }, []);

  const loadSync = useCallback(async () => {
    setSyncLoading(true);
    setSyncError(null);
    try {
      const data = await operationsApi.getSyncLog();
      if (mountedRef.current) setSyncLog(data);
    } catch (e) {
      if (mountedRef.current)
        setSyncError(e instanceof Error ? e.message : 'Failed to load the catalog sync log.');
    } finally {
      if (mountedRef.current) setSyncLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.allSettled([loadSummary(), loadRate(), loadJobs(), loadSync()]);
  }, [loadSummary, loadRate, loadJobs, loadSync]);

  // ── Derived card inputs (summary-first, concrete-dataset fallback) ────
  const nightly = useMemo(() => {
    const n = summary?.last_nightly;
    if (n && n.status) return n;
    const latest = jobs[0];
    if (latest) {
      return {
        job_name: latest.job_name,
        status: latest.status,
        finished_at: latest.finished_at,
        duration_ms: latest.duration_ms,
        scope_done: latest.scope_done,
        scope_total: latest.scope_total,
      };
    }
    return null;
  }, [summary, jobs]);

  const catalog = useMemo(() => {
    const c = summary?.catalog;
    if (c && (c.last_synced_at || c.status)) return c;
    const latest = syncLog[0];
    if (latest) {
      return {
        last_synced_at: latest.finished_at || latest.started_at || null,
        status: latest.status || null,
        stale: undefined as boolean | undefined,
        sync_age_hours: undefined as number | undefined,
      };
    }
    return null;
  }, [summary, syncLog]);

  const rateValues = useMemo(
    () =>
      rate.map((p) => {
        const b = num(p.blocked);
        const a = num(p.allowed);
        const denom = b + a;
        return denom > 0 ? b / denom : 0;
      }),
    [rate]
  );

  const overallBlockRate = useMemo(() => {
    let blocked = 0;
    let all = 0;
    rate.forEach((p) => {
      blocked += num(p.blocked);
      all += num(p.blocked) + num(p.allowed);
    });
    if (all > 0) return blocked / all;
    // Fall back to the summary-provided block-rate (backend returns it 0..1).
    const r = num(summary?.guardrail?.block_rate);
    if (r > 1) return r / 100;
    return r;
  }, [rate, summary]);

  const pendingSafety = num(summary?.pending_reverifications);

  const catalogStale =
    catalog?.stale ??
    (catalog?.last_synced_at
      ? Date.now() - new Date(catalog.last_synced_at).getTime() > 48 * 3600_000
      : undefined);

  const nightlyPill = statusPill(nightly?.status);

  // ── Jobs table columns ───────────────────────────────────────────────
  const jobColumns: Column<JobRun>[] = [
    {
      id: 'severity',
      label: '',
      width: '6px',
      render: (row) => (
        <Box
          aria-hidden
          sx={{
            width: 4,
            height: 34,
            borderRadius: '2px',
            backgroundColor: stripeColor(row.status),
          }}
        />
      ),
    },
    {
      id: 'job_name',
      label: 'Job',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm, fontWeight: 600 }}>
          {row.job_name}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      align: 'center',
      render: (row) => {
        const p = statusPill(row.status);
        return <Chip label={p.label} size="small" color={p.color} variant={p.color === 'default' ? 'outlined' : 'filled'} />;
      },
    },
    {
      id: 'started_at',
      label: 'Started',
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: typography.sizes.sm }}>{formatDateTime(row.started_at)}</Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {relativeAge(row.started_at)}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'duration_ms',
      label: 'Duration',
      align: 'right',
      render: (row) => formatDuration(row.duration_ms),
    },
    {
      id: 'scope',
      label: 'Scope',
      align: 'right',
      render: (row) => {
        const done = num(row.scope_done);
        const total = num(row.scope_total);
        if (total === 0 && done === 0) return '—';
        return `${done.toLocaleString()} / ${total.toLocaleString()}`;
      },
    },
    {
      id: 'error',
      label: 'Error',
      render: (row) =>
        row.error_code || row.error_message ? (
          <Box sx={{ maxWidth: 260 }}>
            {row.error_code && (
              <Chip label={row.error_code} size="small" color="error" variant="outlined" sx={{ mr: 0.5 }} />
            )}
            {row.error_message && (
              <Typography variant="caption" sx={{ color: colors.error.main, wordBreak: 'break-word' }}>
                {row.error_message}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <BuildCircleIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
            Operations
          </Typography>
          <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
            Nightly jobs, catalog sync, guardrail block-rate, and safety re-verification — read-only.
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● {status === 'up' ? 'Connected' : status}
      </Typography>

      {summaryError && (
        <Box sx={{ mb: 3 }}>
          <SectionError message={summaryError} onRetry={loadSummary} />
        </Box>
      )}

      {/* Metric cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Nightly loop"
            value={nightlyPill.label}
            color={statusTheme(nightly?.status)}
            icon="automation"
            loading={summaryLoading && jobsLoading}
            subtitle={nightly?.finished_at ? `Finished ${relativeAge(nightly.finished_at)}` : 'No recent run'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Catalog sync"
            value={catalog ? (catalogStale ? 'Stale' : 'Fresh') : '—'}
            color={catalogStale ? 'content' : 'revenue'}
            icon="sync"
            loading={summaryLoading && syncLoading}
            subtitle={catalog?.last_synced_at ? `Synced ${relativeAge(catalog.last_synced_at)}` : 'No sync recorded'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* Bespoke card — MetricCard can't host the inline sparkline the mockup shows. */}
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: shadows.sm,
              border: `1px solid`,
              borderColor: 'divider',
              height: '100%',
              minHeight: 160,
            }}
          >
            <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: colors.costs[50],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.costs.main,
                    flexShrink: 0,
                  }}
                >
                  <ShieldIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: typography.fonts.heading,
                      fontSize: typography.sizes.xs,
                      fontWeight: 500,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Block-rate
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: typography.fonts.display,
                      fontSize: typography.sizes['3xl'],
                      fontWeight: 700,
                      color: 'text.primary',
                      lineHeight: 1.1,
                    }}
                  >
                    {rateLoading ? '…' : `${(overallBlockRate * 100).toFixed(1)}%`}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Guardrail, 14d
                </Typography>
                {!rateLoading && <BlockRateSparkline values={rateValues} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Safety re-verif"
            value={String(pendingSafety)}
            color={pendingSafety > 0 ? 'content' : 'default'}
            icon="security"
            loading={summaryLoading}
            subtitle={pendingSafety > 0 ? 'Pending acknowledgement' : 'All acknowledged'}
          />
        </Grid>
      </Grid>

      {rateError && (
        <Box sx={{ mb: 3 }}>
          <SectionError message={`Block-rate unavailable: ${rateError}`} onRetry={loadRate} />
        </Box>
      )}
      {syncError && (
        <Box sx={{ mb: 3 }}>
          <SectionError message={`Catalog sync log unavailable: ${syncError}`} onRetry={loadSync} />
        </Box>
      )}

      {/* Jobs table */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 2 }}>
          Recent job runs
        </Typography>
        {jobsError ? (
          <SectionError message={jobsError} onRetry={loadJobs} />
        ) : (
          <DataTable<JobRun>
            columns={jobColumns}
            data={jobs}
            loading={jobsLoading}
            emptyMessage="No job runs recorded yet"
            emptyIcon="automation"
          />
        )}
      </Box>
    </Box>
  );
};

export default OperationsPage;
